/**
 * Segment event names for payroll product analytics.
 *
 * Backend ledger events (AP-2178) are the source of truth for provider-accepted outcomes.
 * These client events are member *intent*: a click, a UI-accepted mutation, or a UI-visible
 * failure. Do not reuse the server snake_case funnel names (`payroll_run_success`, …) — those
 * still mean "the Gusto webhook finished the run".
 *
 * No dollar amounts, SSNs, or bank numbers in properties. Prefer `action`, `outcome`, `source`.
 */
const PAYROLL_ANALYTICS = {
    OVERVIEW_VIEWED: 'Payroll Overview Viewed',
    OVERVIEW_STATE_RENDERED: 'Payroll Overview State Rendered',
    OVERVIEW_ACTION_CLICKED: 'Payroll Overview Action Clicked',
    RUN_SUBMITTED: 'Payroll Run Submitted',
    RUN_SKIPPED: 'Payroll Run Skipped',
    RUN_CANCELLED: 'Payroll Run Cancelled',
    OFF_CYCLE_CREATED: 'Payroll Off-Cycle Created',
    AUTO_PAYROLL_TOGGLED: 'Payroll AutoPayroll Toggled',
    CONTRACTOR_PAYMENT_CREATED: 'Payroll Contractor Payment Created',
    TEAM_WORKER_INVITED: 'Payroll Team Worker Invited',
    TEAM_WORKER_TERMINATED: 'Payroll Team Worker Terminated',
    TEAM_WORKER_REHIRED: 'Payroll Team Worker Rehired',
    DOCUMENT_VIEWED: 'Payroll Document Viewed',
    DOCUMENT_SIGNED: 'Payroll Document Signed',
    PAY_STUB_VIEWED: 'Payroll Pay Stub Viewed',
    CONTRACTOR_PAYMENT_HISTORY_VIEWED: 'Payroll Contractor Payment History Viewed',
    WORKER_PAYMENT_METHOD_UPDATED: 'Payroll Worker Payment Method Updated',
    FUNDING_ACCOUNT_CHANGED: 'Payroll Funding Account Changed',
    RUN_EDITED: 'Payroll Run Edited',
    CONTRACTOR_PAYMENT_CANCELLED: 'Payroll Contractor Payment Cancelled',
    CONTRACTOR_PAYMENT_RECEIPT_VIEWED: 'Payroll Contractor Payment Receipt Viewed',
    TEAM_WORKER_DELETED: 'Payroll Team Worker Deleted',
    TEAM_WORKER_UPDATED: 'Payroll Team Worker Updated',
    TEAM_WORKER_CONFIRMED: 'Payroll Team Worker Confirmed',
    TEAM_INVITATION_RESENT: 'Payroll Team Invitation Resent',
    WORK_ADDRESS_CHANGED: 'Payroll Work Address Changed',
    ALERT_RESOLVED: 'Payroll Alert Resolved',
    REASONABLE_COMPENSATION_UPDATED: 'Payroll Reasonable Compensation Updated',
    GUSTO_STEP_COMPLETED: 'Payroll Gusto Step Completed',
    GUSTO_FLOW_COMPLETED: 'Payroll Gusto Flow Completed',
    PAGE_VIEWED: 'Payroll Page Viewed',
    OFF_CYCLE_STARTED: 'Payroll Off-Cycle Started',
    TIME_OFF_POLICY_ACTION: 'Payroll Time Off Policy Action',
    TEAM_FAMILY_UPDATED: 'Payroll Team Family Updated',
} as const;

export type PayrollAnalyticsEvent = (typeof PAYROLL_ANALYTICS)[keyof typeof PAYROLL_ANALYTICS];

/** `in_flight`: the server already holds the same command and the client resumed polling it. */
export type PayrollAnalyticsOutcome = 'accepted' | 'failed' | 'in_flight';

/** The surface the member acted from, so one event can be split by where it happened. */
export type PayrollAnalyticsSource =
    | 'overview'
    | 'pay_schedules'
    | 'gusto_store'
    | 'contractor_payments'
    | 'team'
    | 'run_payroll'
    | 'documents'
    | 'setup';

export default PAYROLL_ANALYTICS;
